<script setup lang="ts">
import { useEvent } from "@/components/events/useEvent";
import { useUserModeChange } from "@/store/useUserModeChange";
import { pb } from "@/utils/PocketBaseAdapter";
import { requiredValidator } from "@validators";
import { VForm } from "vuetify/components/VForm";
import { moment } from "@/utils/useTimeZone";

const props = defineProps<{
  recurring: any;
  formDate: string | null;
  locationList: any[];
  currentLocation: string;
}>();

const emit = defineEmits<{
  updateErrorMessage: [message: string];
}>();

const formRef = ref<InstanceType<typeof VForm>>();

const userMode = useUserModeChange();
const { isUserMode } = storeToRefs(userMode);
const events = useEvent();
const { recurringWeeks, getRecurringDates } = events;

const getMaxBookAhead = computed(() => {
  const location = props.locationList?.find((l: any) => l?.id === props.currentLocation);
  if (pb.authStore.model?.roles?.length > 0 && !isUserMode.value) return 52;
  return props.recurring?.interval === "Weeks"
    ? location?.maxBookAheadWeeks
    : props.recurring?.interval === "Months"
    ? location?.maxBookAheadWeeks / 4
    : 0;
});

const allowedDatesRecurring = (date: any) => {
  const day = moment(date).startOf('day');
  const today = moment().startOf('day');
  if (!day.isSameOrAfter(today)) return false;
  const maxWeeks = getMaxBookAhead.value;
  if (!maxWeeks) return true;
  return day.isSameOrBefore(today.clone().add(maxWeeks, "weeks"));
};

const recurringDatesList = computed(() => {
  const dates = getRecurringDates.value(props.recurring);
  if (!Array.isArray(dates) || !dates.length || !props.formDate) return dates ?? [];
  const checkDate = new Date(props.formDate.toString());
  return dates.filter(dd => new Date(dd.toString()) >= checkDate);
});

const selectRecurringItem = (item: any) => {
  const { days } = props.recurring || {};
  const maxItems = getMaxBookAhead.value || 0;
  if (!days?.includes(item?.id) && days?.length === maxItems) {
    emit("updateErrorMessage", `You can only select up to ${maxItems}.`);
    return;
  }
  const index = days?.findIndex((i: any) => i === item.id);
  if (index !== -1) props.recurring?.days?.splice(index, 1);
  else props.recurring?.days?.push(item.id);
};

defineExpose({
  validate: () => formRef.value?.validate(),
});
</script>

<template>
  <VForm ref="formRef">
    <VWindowItem>
      <VRow>
        <VCol lg="7" md="7" sm="7" cols="12">
          <div class="mb-6">Select the Recurring configuration</div>
          <VRow>
            <VCol lg="3" md="3" sm="6" xs="6" cols="12">
              <VTextField
                v-model="recurring.frequency"
                :rules="[requiredValidator]"
                label="Repeat"
              />
            </VCol>
            <VCol lg="4" md="4" sm="6" xs="6" cols="12">
              <VSelect
                v-model="recurring.interval"
                :items="['Weeks', 'Months']"
                :rules="[requiredValidator]"
                label="Interval"
                @update:modelValue="recurring.days = []"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="12">
              <VBtn
                v-for="item in recurringWeeks"
                icon=""
                :key="item.id"
                :color="recurring?.days?.includes(item?.id) ? 'primary' : 'grey-400'"
                size="small"
                class="rounded ml-1"
                @click="selectRecurringItem(item)"
              >
                {{ item.label }}
                <VTooltip activator="parent" location="top" open-delay="500">
                  {{ item?.value }}
                </VTooltip>
              </VBtn>
            </VCol>
          </VRow>
          <VRow>
            <VCol lg="8" md="8" sm="12" xs="12" cols="12">
              <DateInput
                v-model="recurring.endDate"
                label="Ends on"
                :rules="[requiredValidator]"
                format="dddd, Do MMM YYYY"
                :allowed-dates="allowedDatesRecurring"
              />
            </VCol>
          </VRow>
        </VCol>
        <VCol cols="5" class="inline_picker_selected_date">
          <v-date-picker
            :model-value="recurringDatesList"
            multiple
            readonly
            hide-header
            show-adjacent-months
            elevation="5"
          />
        </VCol>
      </VRow>
    </VWindowItem>
  </VForm>
</template>
<style lang="scss">
  @media (max-width: 720px) {
  .inline_picker_selected_date {
    display: none;
  }
}
</style>
